import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import Modal, { ModalClose } from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody';
import SfhSecondaryButton from '../SellWizard/SfhSecondaryButton';
import { pxToRem, mediaBelow } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import themeColors from '../../../assets/styles/themeColors';

const ModalBodyWrap = styled.div`
  position: relative;
`;

const AbsoluteClose = styled(ModalClose)`
  position: absolute;
  top: 0;
  right: 0;

  &,
  svg {
    width: 20px;
    height: 20px;
  }

  /* close.svg X spans 8/16 of the viewBox → 10px at 20px; scale up to 20px */
  svg > g {
    transform: scale(1.25);
    transform-origin: center;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${pxToRem(16)};
`;

const BodyTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BodyTitle = styled.h3`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin-bottom: ${pxToRem(8)};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

const BodyDescription = styled.p`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  margin: 0;
  white-space: pre-line;
  display: flex;
  justify-content: center;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    max-width: unset;
  }
  max-width: 324px;
`;

const PhoneLink = styled.a`
  display: block;
  margin-top: ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  text-align: center;
  text-decoration: underline;
  color: ${themeColors.colorPrimary};
`;

const CloseButton = styled(SfhSecondaryButton)`
  && {
    width: 100%;
    margin-top: ${pxToRem(16)};
    font-family: ${variables.family.goldSilverHeaders}, sans-serif;
    line-height: 1.3;
  }

  &&,
  &&:hover,
  &&:focus,
  &&:active {
    color: ${themeColors.colorSfhActionBlue};
  }
`;

const IraUtmaAccountRestrictionModal = ({
  isOpen,
  close,
  title,
  description,
  dataCyPrefix = 'button-sfh-ira-utma',
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title || t('portfolio.sellFromHome.iraUtma.title');
  const resolvedDescription =
    description || t('portfolio.sellFromHome.iraUtma.description');

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      borderRadius="10px"
      close={close}
      hideHeader
      verticalCenter
      isCenterModal
      disableBlur
      maxWidth="600px"
    >
      <ModalBody>
        <ModalBodyWrap>
          <AbsoluteClose
            type="button"
            onClick={close}
            aria-label={t('common.close')}
            data-cy={`${dataCyPrefix}-close-icon`}
          >
            <Close width={20} height={20} />
          </AbsoluteClose>
          <IconWrapper>
            <InfoIcon width={44} height={44} />
          </IconWrapper>
          <BodyTitleWrapper>
            <BodyTitle>{resolvedTitle}</BodyTitle>
            <BodyDescription>{resolvedDescription}</BodyDescription>
          </BodyTitleWrapper>
          <PhoneLink
            href={`tel:${t('portfolio.sellFromHome.iraUtma.phoneNumber')}`}
          >
            {t('portfolio.sellFromHome.iraUtma.needHelp')}{' '}
            {t('portfolio.sellFromHome.iraUtma.phone')}
          </PhoneLink>
          <CloseButton onClick={close} data-cy={`${dataCyPrefix}-close`}>
            {t('common.close')}
          </CloseButton>
        </ModalBodyWrap>
      </ModalBody>
    </Modal>
  );
};

IraUtmaAccountRestrictionModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
  dataCyPrefix: PropTypes.string,
};

export default IraUtmaAccountRestrictionModal;
